import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import useTranslation from '../../hooks/useTranslation';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const { error: resetError } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/reset-password`,
      });

      if (resetError) {
        setError(resetError?.message || 'Failed to send reset email. Please try again.');
      } else {
        setSuccess(true);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <Helmet>
        <title>Forgot Password - OnSitePM</title>
      </Helmet>
      {/* LEFT PANEL — Branding */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col items-center justify-center"
        style={{ background: 'linear-gradient(145deg, #1a2535 0%, #2C3E50 50%, #1e3a5f 100%)' }}
      >
        {/* Blueprint grid overlay */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,107,44,0.6) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,107,44,0.6) 1px, transparent 1px)
            `,
            backgroundSize: '48px 48px',
          }}
        />

        {/* Decorative corners */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-16 h-16 border-t-2 border-l-2 opacity-20" style={{ borderColor: '#FF6B2C' }} />
          <div className="absolute bottom-10 right-10 w-16 h-16 border-b-2 border-r-2 opacity-20" style={{ borderColor: '#FF6B2C' }} />
          <div className="absolute top-0 right-0 w-px h-full opacity-10" style={{ background: 'linear-gradient(to bottom, transparent, #FF6B2C, transparent)' }} />
        </div>

        {/* Centered branding */}
        <div className="relative z-10 text-center px-12 max-w-md">
          <div className="flex items-center justify-center mb-8">
            <div className="relative">
              <div
                className="w-20 h-20 rounded-2xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #FF6B2C 0%, #E55A1F 100%)', boxShadow: '0 8px 32px rgba(255,107,44,0.4)' }}
              >
                <Icon name="HardHat" size={36} color="#FFFFFF" />
              </div>
              <div
                className="absolute -bottom-1 -right-1 w-6 h-6 rounded-md flex items-center justify-center"
                style={{ background: '#2C3E50', border: '2px solid #FF6B2C' }}
              >
                <Icon name="Building2" size={12} color="#FF6B2C" />
              </div>
            </div>
          </div>

          <h1 className="text-5xl font-bold text-white mb-3 tracking-tight">
            OnSite<span style={{ color: '#FF6B2C' }}>PM</span>
            <sup style={{ fontSize: '0.45em', verticalAlign: 'super', lineHeight: 0, color: '#FF6B2C' }}>™</sup>
          </h1>
          <p className="text-lg font-medium mb-2" style={{ color: 'rgba(255,255,255,0.75)' }}>
            Construction Project Management
          </p>
          <div
            className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full"
            style={{ background: 'rgba(255,107,44,0.12)', border: '1px solid rgba(255,107,44,0.25)' }}
          >
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#FF6B2C' }} />
            <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.55)' }}>
              {t('login.poweredBy')}
            </span>
          </div>
        </div>
      </div>
      {/* RIGHT PANEL */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-md">

          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #FF6B2C 0%, #E55A1F 100%)' }}
            >
              <Icon name="HardHat" size={22} color="#FFFFFF" />
            </div>
            <div className="ml-3">
              <span className="text-2xl font-bold text-foreground">
                OnSite<span style={{ color: '#FF6B2C' }}>PM</span>
                <sup style={{ fontSize: '0.45em', verticalAlign: 'super', lineHeight: 0, color: '#FF6B2C' }}>™</sup>
              </span>
            </div>
          </div>

          <div className="bg-card rounded-2xl border border-border p-8 shadow-subtle">

            {/* Card header */}
            <div className="mb-8">
              <div className="hidden lg:flex items-center gap-3 mb-6">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #FF6B2C 0%, #E55A1F 100%)' }}
                >
                  <Icon name="HardHat" size={16} color="#FFFFFF" />
                </div>
                <span className="text-lg font-bold text-foreground">
                  OnSite<span style={{ color: '#FF6B2C' }}>PM</span>
                  <sup style={{ fontSize: '0.45em', verticalAlign: 'super', lineHeight: 0, color: '#FF6B2C' }}>™</sup>
                </span>
              </div>

              <div className="flex items-center gap-3 mb-2">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: 'rgba(255,107,44,0.1)', border: '1px solid rgba(255,107,44,0.2)' }}
                >
                  <Icon name="KeyRound" size={20} color="#FF6B2C" />
                </div>
                <h2 className="text-2xl font-semibold text-foreground">Forgot Password</h2>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Enter your email and we'll send you a link to reset your password.
              </p>
            </div>

            {success ? (
              /* Success state */
              (<div className="space-y-5">
                <div className="flex flex-col items-center text-center p-6 rounded-xl bg-green-50 border border-green-200">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-3">
                    <Icon name="MailCheck" size={24} color="#16a34a" />
                  </div>
                  <h3 className="text-base font-semibold text-green-800 mb-1">Check your inbox</h3>
                  <p className="text-sm text-green-700">
                    We sent a password reset link to <strong>{email}</strong>. Check your email and follow the instructions.
                  </p>
                </div>
                <p className="text-xs text-center text-muted-foreground">
                  Didn't receive it? Check your spam folder or{' '}
                  <button
                    type="button"
                    onClick={() => setSuccess(false)}
                    className="font-semibold"
                    style={{ color: '#FF6B2C' }}
                  >
                    try again
                  </button>
                  .
                </p>
                <Button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="w-full flex items-center justify-center space-x-2"
                >
                  <Icon name="ArrowLeft" size={16} />
                  <span>Back to Sign In</span>
                </Button>
              </div>)
            ) : (
              /* Form state */
              (<form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e?.target?.value); setError(''); }}
                  placeholder="you@company.com"
                  required
                  autoComplete="email"
                />
                {error && (
                  <div className="flex items-center space-x-2 p-3 rounded-lg bg-red-50 border border-red-200">
                    <Icon name="AlertCircle" size={16} className="text-red-600" />
                    <span className="text-sm text-red-600">{error}</span>
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={isSubmitting || !email}
                  className="w-full flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <Icon name="Loader2" size={18} className="animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Icon name="Send" size={18} />
                      <span>Send Reset Link</span>
                    </>
                  )}
                </Button>
                <p className="text-xs text-center mt-3" style={{ color: 'var(--muted-foreground)' }}>
                  Remember your password?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="font-semibold"
                    style={{ color: '#FF6B2C' }}
                  >
                    Sign In
                  </button>
                </p>
              </form>)
            )}
          </div>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-xs font-semibold text-foreground">
              OnSitePM<sup style={{ fontSize: '0.55em', verticalAlign: 'super', lineHeight: 0 }}>™</sup>
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">{t('login.poweredBy')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
